/* eslint-disable camelcase */
import i18n from 'i18next';
import { Settings } from 'luxon';
import numeral from 'numeral';

export enum LocaleCode {
    cs_CZ = 'cs_CZ',
    de_CH = 'de_CH',
    de_DE = 'de_DE',
    en_GB = 'en_GB',
    en_US = 'en_US',
    es_ES = 'es_ES',
    es_MX = 'es_MX',
    fr_CA = 'fr_CA',
    fr_FR = 'fr_FR',
    hu_HU = 'hu_HU',
    it_IT = 'it_IT',
    ko_KR = 'ko_KR',
    nl_NL = 'nl_NL',
    pl_PL = 'pl_PL',
    pt_BR = 'pt_BR',
    pt_PT = 'pt_PT',
    ro_RO = 'ro_RO',
    ru_RU = 'ru_RU',
    sk_SK = 'sk_SK',
    tr_TR = 'tr_TR',
    uk_UK = 'uk_UK',
}

export interface LocaleOption {
    value: LocaleCode;
    label: string;
}

interface LocalConfig {
    locale: LocaleCode;
    luxonLocale: string;
    numeralLocale: string;
    fallbackCountryCode: string[];
}

interface NumeralLocaleConfig {
    delimiters: {
        thousands: string;
        decimal: string;
    };
    abbreviations: {
        thousand: string;
        million: string;
        billion: string;
        trillion: string;
    };
    ordinal: (number: number) => string;
    currency: {
        symbol: string;
    };
}

const DEFAULT_LOCALE = LocaleCode.en_US;

export const LocaleOptions: LocaleOption[] = [
    {
        value: LocaleCode.cs_CZ,
        label: 'Čeština (Česká republika)',
    },
    {
        value: LocaleCode.de_CH,
        label: 'Deutsch (Schweiz)',
    },
    {
        value: LocaleCode.de_DE,
        label: 'Deutsch (Deutschland)',
    },
    {
        value: LocaleCode.es_ES,
        label: 'Español (España)',
    },
    {
        value: LocaleCode.es_MX,
        label: 'Español (México)',
    },
    {
        value: LocaleCode.en_GB,
        label: 'English (United Kingdom)',
    },
    {
        value: LocaleCode.en_US,
        label: 'English (United States)',
    },
    {
        value: LocaleCode.fr_CA,
        label: 'Français (Canada)',
    },
    {
        value: LocaleCode.fr_FR,
        label: 'Français (France)',
    },
    {
        value: LocaleCode.hu_HU,
        label: 'Magyar (Magyarország)',
    },
    {
        value: LocaleCode.it_IT,
        label: 'Italiano (Italia)',
    },
    {
        value: LocaleCode.ko_KR,
        label: '한국어 (대한민국)',
    },
    {
        value: LocaleCode.nl_NL,
        label: 'Nederlands (Nederland)',
    },
    {
        value: LocaleCode.pl_PL,
        label: 'Polski (Polska)',
    },
    {
        value: LocaleCode.pt_BR,
        label: 'Português (Brazil)',
    },
    {
        value: LocaleCode.pt_PT,
        label: 'Português (Portugal)',
    },
    {
        value: LocaleCode.ro_RO,
        label: 'Română (România)',
    },
    {
        value: LocaleCode.ru_RU,
        label: 'Pусский (Россия)',
    },
    {
        value: LocaleCode.sk_SK,
        label: 'Slovenčina (Slovensko)',
    },
    {
        value: LocaleCode.tr_TR,
        label: 'Türkçe (Türkiye)',
    },
    {
        value: LocaleCode.uk_UK,
        label: 'Українська',
    },
];

const LocaleConfigs: Record<LocaleCode | string, LocalConfig> = {
    'cs_*': {
        locale: LocaleCode.cs_CZ,
        luxonLocale: 'cs',
        numeralLocale: 'cs_cz',
        fallbackCountryCode: ['cs'],
    },
    [LocaleCode.de_CH]: {
        locale: LocaleCode.de_CH,
        luxonLocale: 'de-CH', // IETF locale code with region
        numeralLocale: 'de_ch',
        fallbackCountryCode: ['ch'], // ISO country code
    },
    'de_*': {
        locale: LocaleCode.de_DE,
        luxonLocale: 'de',
        numeralLocale: 'de_de',
        fallbackCountryCode: ['de'],
    },
    [LocaleCode.en_GB]: {
        locale: LocaleCode.en_GB,
        luxonLocale: 'en-GB',
        numeralLocale: 'en_gb',
        fallbackCountryCode: ['gb'],
    },
    'en_*': {
        locale: LocaleCode.en_US,
        luxonLocale: 'en-US',
        numeralLocale: 'en_us',
        fallbackCountryCode: ['us'],
    },
    [LocaleCode.es_MX]: {
        locale: LocaleCode.es_MX,
        luxonLocale: 'es-MX',
        numeralLocale: 'es_mx',
        fallbackCountryCode: ['mx'],
    },
    'es_*': {
        locale: LocaleCode.es_ES,
        luxonLocale: 'es',
        numeralLocale: 'es_es',
        fallbackCountryCode: ['es'],
    },
    [LocaleCode.fr_CA]: {
        locale: LocaleCode.fr_CA,
        luxonLocale: 'fr-CA',
        numeralLocale: 'fr_ca',
        fallbackCountryCode: ['ca'],
    },
    'fr_*': {
        locale: LocaleCode.fr_FR,
        luxonLocale: 'fr',
        numeralLocale: 'fr_fr',
        fallbackCountryCode: ['fr'],
    },
    'hu_*': {
        locale: LocaleCode.hu_HU,
        luxonLocale: 'hu',
        numeralLocale: 'hu_hu',
        fallbackCountryCode: ['hu'],
    },
    'it_*': {
        locale: LocaleCode.it_IT,
        luxonLocale: 'it',
        numeralLocale: 'it_it',
        fallbackCountryCode: ['it'],
    },
    [LocaleCode.ko_KR]: {
        locale: LocaleCode.ko_KR,
        luxonLocale: 'ko',
        numeralLocale: 'ko_kr',
        fallbackCountryCode: ['kr'],
    },
    'nl_*': {
        locale: LocaleCode.nl_NL,
        luxonLocale: 'nl',
        numeralLocale: 'nl_nl',
        fallbackCountryCode: ['nl'],
    },
    [LocaleCode.pt_BR]: {
        locale: LocaleCode.pt_BR,
        luxonLocale: 'pt-BR',
        numeralLocale: 'pt_br',
        fallbackCountryCode: ['br'],
    },
    'pl_*': {
        locale: LocaleCode.pl_PL,
        luxonLocale: 'pl',
        numeralLocale: 'pl_pl',
        fallbackCountryCode: ['pl'],
    },
    'pt_*': {
        locale: LocaleCode.pt_PT,
        luxonLocale: 'pt',
        numeralLocale: 'pt_pt',
        fallbackCountryCode: ['pt'],
    },
    'ro_*': {
        locale: LocaleCode.ro_RO,
        luxonLocale: 'ro',
        numeralLocale: 'ro_ro',
        fallbackCountryCode: ['ro'],
    },
    'ru_*': {
        locale: LocaleCode.ru_RU,
        luxonLocale: 'ru',
        numeralLocale: 'ru_ru',
        fallbackCountryCode: ['ru'],
    },
    'sk_*': {
        locale: LocaleCode.sk_SK,
        luxonLocale: 'sk',
        numeralLocale: 'sk_sk',
        fallbackCountryCode: ['sk'],
    },
    'tr_*': {
        locale: LocaleCode.tr_TR,
        luxonLocale: 'tr',
        numeralLocale: 'tr_tr',
        fallbackCountryCode: ['tr'],
    },
    'uk_*': {
        locale: LocaleCode.uk_UK,
        luxonLocale: 'uk',
        numeralLocale: 'uk_uk',
        fallbackCountryCode: ['uk'],
    },
};

const NumeralLocales: Record<LocaleCode, NumeralLocaleConfig> = {
    [LocaleCode.cs_CZ]: {
        delimiters: { thousands: ' ', decimal: ',' },
        abbreviations: { thousand: 'tis.', million: 'mil.', billion: 'mld.', trillion: 'bil.' },
        ordinal: () => '.',
        currency: { symbol: 'Kč' },
    },
    [LocaleCode.de_CH]: {
        delimiters: { thousands: '’', decimal: '.' },
        abbreviations: { thousand: 'k', million: 'Mio', billion: 'Mrd', trillion: 'Bio' },
        ordinal: () => '.',
        currency: { symbol: 'CHF' },
    },
    [LocaleCode.de_DE]: {
        delimiters: { thousands: '.', decimal: ',' },
        abbreviations: { thousand: 'k', million: 'Mio', billion: 'Mrd', trillion: 'Bio' },
        ordinal: () => '.',
        currency: { symbol: '€' },
    },
    [LocaleCode.en_GB]: {
        delimiters: { thousands: ',', decimal: '.' },
        abbreviations: { thousand: 'k', million: 'm', billion: 'b', trillion: 't' },
        ordinal: (number: any) => {
            const b = number % 10;
            const isTeen = Math.floor((number % 100) / 10) === 1;

            if (isTeen) return 'th';
            if (b === 1) return 'st';
            if (b === 2) return 'nd';
            if (b === 3) return 'rd';
            return 'th';
        },
        currency: { symbol: '£' },
    },
    [LocaleCode.en_US]: {
        delimiters: { thousands: ',', decimal: '.' },
        abbreviations: { thousand: 'k', million: 'm', billion: 'b', trillion: 't' },
        ordinal: (number: any) => {
            const b = number % 10;
            const isTeen = Math.floor((number % 100) / 10) === 1;

            if (isTeen) return 'th';
            if (b === 1) return 'st';
            if (b === 2) return 'nd';
            if (b === 3) return 'rd';
            return 'th';
        },
        currency: { symbol: '$' },
    },
    [LocaleCode.es_ES]: {
        delimiters: { thousands: '.', decimal: ',' },
        abbreviations: { thousand: 'mil', million: 'M', billion: 'MM', trillion: 'T' },
        ordinal: () => 'º',
        currency: { symbol: '€' },
    },
    [LocaleCode.es_MX]: {
        delimiters: { thousands: ',', decimal: '.' },
        abbreviations: { thousand: 'mil', million: 'M', billion: 'MM', trillion: 'T' },
        ordinal: () => 'º',
        currency: { symbol: '$' },
    },
    [LocaleCode.fr_CA]: {
        delimiters: { thousands: ' ', decimal: ',' },
        abbreviations: { thousand: 'k', million: 'M', billion: 'G', trillion: 'T' },
        ordinal: () => 'ᵉʳ',
        currency: { symbol: '$' },
    },
    [LocaleCode.fr_FR]: {
        delimiters: { thousands: ' ', decimal: ',' },
        abbreviations: { thousand: 'k', million: 'M', billion: 'Md', trillion: 'Bn' },
        ordinal: (number: any) => (number === 1 ? 'er' : 'ème'),
        currency: { symbol: '€' },
    },
    [LocaleCode.hu_HU]: {
        delimiters: { thousands: ' ', decimal: ',' },
        abbreviations: { thousand: 'E', million: 'M', billion: 'Mrd', trillion: 'T' },
        ordinal: () => '.',
        currency: { symbol: 'Ft' },
    },
    [LocaleCode.it_IT]: {
        delimiters: { thousands: '.', decimal: ',' },
        abbreviations: { thousand: 'mila', million: 'mil', billion: 'mld', trillion: 'tril' },
        ordinal: () => 'º',
        currency: { symbol: '€' },
    },
    [LocaleCode.ko_KR]: {
        delimiters: { thousands: ',', decimal: '.' },
        abbreviations: { thousand: '천', million: '백만', billion: '십억', trillion: '조' },
        ordinal: () => '번째',
        currency: { symbol: '₩' },
    },
    [LocaleCode.nl_NL]: {
        delimiters: { thousands: '.', decimal: ',' },
        abbreviations: { thousand: 'k', million: 'mln', billion: 'mrd', trillion: 'bln' },
        ordinal: () => 'e',
        currency: { symbol: '€' },
    },
    [LocaleCode.pl_PL]: {
        delimiters: { thousands: ' ', decimal: ',' },
        abbreviations: { thousand: 'tys.', million: 'mln', billion: 'mld', trillion: 'bln' },
        ordinal: () => '.',
        currency: { symbol: 'zł' },
    },
    [LocaleCode.pt_BR]: {
        delimiters: { thousands: '.', decimal: ',' },
        abbreviations: { thousand: 'mil', million: 'mi', billion: 'bi', trillion: 'tri' },
        ordinal: () => 'º',
        currency: { symbol: 'R$' },
    },
    [LocaleCode.pt_PT]: {
        delimiters: { thousands: ' ', decimal: ',' },
        abbreviations: { thousand: 'mil', million: 'M', billion: 'MM', trillion: 'T' },
        ordinal: () => 'º',
        currency: { symbol: '€' },
    },
    [LocaleCode.ro_RO]: {
        delimiters: { thousands: '.', decimal: ',' },
        abbreviations: { thousand: 'mi', million: 'mil', billion: 'mld', trillion: 'trl' },
        ordinal: () => '.',
        currency: { symbol: 'lei' },
    },
    [LocaleCode.ru_RU]: {
        delimiters: { thousands: ' ', decimal: ',' },
        abbreviations: { thousand: 'тыс.', million: 'млн', billion: 'млрд', trillion: 'трлн' },
        ordinal: () => '-й',
        currency: { symbol: '₽' },
    },
    [LocaleCode.sk_SK]: {
        delimiters: { thousands: ' ', decimal: ',' },
        abbreviations: { thousand: 'tis.', million: 'mil.', billion: 'mld.', trillion: 'bil.' },
        ordinal: () => '.',
        currency: { symbol: '€' },
    },
    [LocaleCode.tr_TR]: {
        delimiters: { thousands: '.', decimal: ',' },
        abbreviations: { thousand: 'B', million: 'M', billion: 'Milyar', trillion: 'Trilyon' },
        ordinal: () => '.',
        currency: { symbol: '₺' },
    },
    [LocaleCode.uk_UK]: {
        delimiters: { thousands: ' ', decimal: ',' },
        abbreviations: { thousand: 'тис.', million: 'млн', billion: 'млрд', trillion: 'трлн' },
        ordinal: () => '-й',
        currency: { symbol: '₴' },
    },
};

export const setLocale = (locale: LocaleCode): void => {
    const localeCodes = Object.keys(LocaleConfigs);
    const localeCodeMatch = localeCodes.find((lc) => new RegExp(`^${lc.replace('*', '.*')}`).test(locale));
    const localeConfig = localeCodeMatch ? LocaleConfigs[localeCodeMatch] : LocaleConfigs['en_*'];

    // Set i18n language
    i18n.changeLanguage(locale);

    // Set Luxon locale
    Settings.defaultLocale = localeConfig.luxonLocale;

    try {
        // Register numeral locales
        Object.entries(NumeralLocales).forEach(([localeCode, config]) => {
            numeral.register('locale', localeCode.toLowerCase(), config);
        });

        // Set Numeral locale
        const numeralLocale = numeral.locales[localeConfig.numeralLocale]
            ? localeConfig.numeralLocale
            : DEFAULT_LOCALE.toLowerCase();

        numeral.locale(numeralLocale);
    } catch (error) {
        console.error('Locale configuration failed:', error);
        // Fallback to defaults
        Settings.defaultLocale = LocaleConfigs[DEFAULT_LOCALE].luxonLocale;
        numeral.locale(DEFAULT_LOCALE.toLowerCase());
    }
};

export const getLabelForLocaleCode = (localeCode: string): string => {
    switch (localeCode) {
        case 'af':
            return 'Afrikaans';
        case 'am':
            return 'አማርኛ';
        case 'ar':
            return 'العربية';
        case 'ar_MA':
            return '(العربية (المغرب';
        case 'ar_US':
            return 'العربية (العالم)';
        case 'as':
            return 'অসমীয়া';
        case 'as_IN':
            return 'অসমীয়া (ভারত)';
        case 'az':
            return 'Azərbaycanca / آذربايجان';
        case 'az_AZ':
            return 'Azərbaycan dili (Azərbaycan)';
        case 'be':
            return 'Беларуская';
        case 'bg':
            return 'Български';
        case 'bg_BG':
            return 'Български (България)';
        case 'bn':
            return 'বাংলা';
        case 'bs':
            return 'Bosanski';
        case 'bs_BA':
            return 'Bosanski (Bosna i Hercegovina)';
        case 'ca':
            return 'Català';
        case 'ca_ES':
            return 'Català (Espanya)';
        case 'cs':
            return 'Česky';
        case 'cs_CZ':
            return 'Čeština (Česká republika)';
        case 'cy':
            return 'Cymraeg';
        case 'da':
            return 'Dansk';
        case 'da_DK':
            return 'Dansk (Danmark)';
        case 'de':
            return 'Deutsch';
        case 'de_AT':
            return 'Deutsch (Österreich)';
        case 'de_CH':
            return 'Deutsch (Schweiz)';
        case 'de_DE':
            return 'Deutsch (Deutschland)';
        case 'el':
            return 'Ελληνικά';
        case 'el_GR':
            return 'Ελληνικά (Ελλάδα)';
        case 'en':
            return 'English';
        case 'en_AE':
            return 'English (United Arab Emirates)';
        case 'en_CA':
            return 'English (Canada)';
        case 'en_GB':
            return 'English (United Kingdom)';
        case 'en_GY':
            return 'English (Guyana)';
        case 'en_IE':
            return 'English (Ireland)';
        case 'en_IN':
            return 'English (India)';
        case 'en_NZ':
            return 'English (New Zealand)';
        case 'en_SG':
            return 'English (Singapore)';
        case 'en_US':
            return 'English (United States)';
        case 'es':
            return 'Español';
        case 'es_419':
            return 'Español (Latinoamérica)';
        case 'es_AR':
            return 'Español (Argentina)';
        case 'es_CO':
            return 'Español (Colombia)';
        case 'es_ES':
            return 'Español (España)';
        case 'es_MX':
            return 'Español (México)';
        case 'et':
            return 'Eesti';
        case 'et_EE':
            return 'Eesti';
        case 'eu':
            return 'Euskara';
        case 'fa':
            return 'فارسی';
        case 'fi':
            return 'Suomi';
        case 'fi_FI':
            return 'Suomi';
        case 'fil':
            return 'Filipino';
        case 'fr':
            return 'Français';
        case 'fr_BE':
            return 'Français (Belgique)';
        case 'fr_CA':
            return 'Français (Canada)';
        case 'fr_CH':
            return 'Français (Suisse)';
        case 'fr_FR':
            return 'Français (France)';
        case 'fr_MA':
            return 'Français (Maroc)';
        case 'ga_IE':
            return 'Gaeilge (Éire)';
        case 'gl':
            return 'Galego';
        case 'gu':
            return 'ગુજરાતી';
        case 'he':
            return 'עברית';
        case 'he_IL':
            return 'עברית (ישראל)';
        case 'hi':
            return 'हिन्दी';
        case 'hi_IN':
            return 'हिन्दी (भारत)';
        case 'hr':
            return 'Hrvatski';
        case 'hr_HR':
            return 'Hrvatski (Hrvatska)';
        case 'ht':
            return 'Kreyòl Ayisyen';
        case 'ht_US':
            return 'Kreyòl Ayisyen (Etazini)';
        case 'hu':
            return 'Magyar';
        case 'hu_HU':
            return 'Magyar (Magyarország)';
        case 'hy':
            return 'Հայերեն';
        case 'hy_AM':
            return 'Հայերեն (Հայաստան)';
        case 'id':
            return 'Bahasa Indonesia';
        case 'id_ID':
            return 'Bahasa Indonesia (Indonesia)';
        case 'is':
            return 'Íslenska';
        case 'is_IS':
            return 'Íslenska (Ísland)';
        case 'it':
            return 'Italiano';
        case 'it_CH':
            return 'Italiano (Svizzera)';
        case 'it_IT':
            return 'Italiano (Italia)';
        case 'ja':
            return '日本語';
        case 'ja_JP':
            return '日本語 (日本)';
        case 'ka':
            return 'ქართული';
        case 'ka_GE':
            return 'ქართული (საქართველო)';
        case 'kk':
            return 'Қазақша';
        case 'km':
            return 'ភាសាខ្មែរ';
        case 'kn':
            return 'ಕನ್ನಡ';
        case 'ko':
            return '한국어';
        case 'ko_KR':
            return '한국어 (대한민국)';
        case 'lb':
            return 'Lëtzebuergesch';
        case 'lb_LU':
            return 'Lëtzebuergesch (Lëtzebuerg)';
        case 'lt':
            return 'Lietuvių';
        case 'lt_LT':
            return 'Lietuvių (Lietuva)';
        case 'lv':
            return 'Latviešu';
        case 'lv_LV':
            return 'Latviešu (Latvija)';
        case 'mk':
            return 'Македонски';
        case 'mk_MK':
            return 'Македонски (Северна Македонија)';
        case 'ml':
            return 'മലയാളം';
        case 'mn':
            return 'Монгол';
        case 'mr':
            return 'मराठी';
        case 'ms':
            return 'Bahasa Melayu';
        case 'ms_MY':
            return 'Melayu (Malaysia)';
        case 'mt_MT':
            return 'Malti (Malta)';
        case 'my':
            return 'Myanmasa';
        case 'ne':
            return 'नेपाली';
        case 'nl':
            return 'Nederlands';
        case 'nl_BE':
            return 'Nederlands (België)';
        case 'nl_NL':
            return 'Nederlands (Nederland)';
        case 'nn':
            return 'Nynorsk';
        case 'nn_NO':
            return 'Nynorsk (Noreg)';
        case 'no':
            return 'Norsk (bokmål / riksmål)';
        case 'no_NO':
            return 'Norsk (Norge)';
        case 'or':
            return 'ଓଡ଼ିଆ';
        case 'or_IN':
            return 'ଓଡ଼ିଆ (ଭାରତ)';
        case 'pa':
            return 'ਪੰਜਾਬੀ / पंजाबी / پنجابي';
        case 'pl':
            return 'Polski';
        case 'pl_PL':
            return 'Polski (Polska)';
        case 'pt':
            return 'Português';
        case 'pt_PT':
            return 'Português (Portugal)';
        case 'pt_BR':
            return 'Português (Brasil)';
        case 'ro':
            return 'Română';
        case 'ro_RO':
            return 'Română (România)';
        case 'ru':
            return 'Русский';
        case 'ru_RU':
            return 'Pусский (Россия)';
        case 'si':
            return 'සිංහල';
        case 'sk':
            return 'Slovenčina';
        case 'sk_SK':
            return 'Slovenčina (Slovensko)';
        case 'sl':
            return 'Slovenščina';
        case 'sl_SI':
            return 'Slovenščina (Slovenija)';
        case 'so':
            return 'Soomaali';
        case 'sq':
            return 'Shqip';
        case 'sq_AL':
            return 'Shqip (Shqipëri)';
        case 'sr':
            return 'Српски';
        case 'sr_ME':
            return 'Crnogorski (Crna Gora)';
        case 'sr_RS':
            return 'Srpski (Srbija)';
        case 'sv':
            return 'Svenska';
        case 'sv_SE':
            return 'Svenska (Sverige)';
        case 'sw':
            return 'Kiswahili';
        case 'sw_US':
            return 'Kiswahili (Āfrika)';
        case 'ta':
            return 'தமிழ்';
        case 'te':
            return 'తెలుగు';
        case 'th':
            return 'ไทย / Phasa Thai';
        case 'th_TH':
            return 'ไทย (ประเทศไทย)';
        case 'tl':
            return 'Tagalog';
        case 'tl_PH':
            return 'Tagalog (Pilipinas)';
        case 'tr':
            return 'Türkçe';
        case 'tr_TR':
            return 'Türkçe (Türkiye)';
        case 'uk':
            return 'Українська';
        case 'uk_UA':
            return 'Українська (Україна)';
        case 'ur':
            return 'اردو';
        case 'vi':
            return 'Việtnam';
        case 'vi_VN':
            return 'Tiếng Việt (Việt Nam)';
        case 'xh_ZA':
            return 'isiXhosa (eMzantsi Afrika)';
        case 'zh':
            return '简体中文';
        case 'zh_CN':
            return '简体中文 (中国)';
        case 'zh_HK':
            return '繁體中文 (香港)';
        case 'zh_TW':
            return '繁體中文 (美國)';
        case 'zh_US':
            return '简体中文 (美国)';
        case 'zu_ZA':
            return 'isiZulu (iNingizimu Afrika)';
        default:
            return localeCode;
    }
};

export const getLuxonLocale = (locale: LocaleCode) => {
    const localeValues = Object.values(LocaleConfigs);

    const filteredLocaleConfig = localeValues.find((localeValue) => localeValue.locale === locale);

    return filteredLocaleConfig?.luxonLocale;
};
